import { ArrowRight, ChartNoAxesCombined, Download, MonitorPlay, ScanSearch, WandSparkles, type LucideIcon } from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useToolsInfo } from '../api/tools'
import { useUser } from '../auth/context'
import { PageHeader } from '../components/PageHeader'
import { AnalyzeDialog } from '../components/tools/AnalyzeDialog'
import { RecentJobs } from '../components/tools/RecentJobs'
import { ReelDialog } from '../components/tools/ReelDialog'
import { YoutubeDialog } from '../components/tools/YoutubeDialog'
import { useIsDesktop } from '../lib/useIsDesktop'
import { isInTelegram } from '../telegram'
import { useViewIsActive, useViewState } from '../lib/navigation'
import { useBack } from '../lib/useBack'
import { PendingScreenshotsCard, ScreenshotQueueDialog } from '../components/analytics/ScreenshotQueue'

type ToolDialog = 'analyze' | 'reels' | 'youtube'

interface ToolCard {
  id: string
  icon: LucideIcon
  title: string
  text: string
  action: string
  /** Скачивание: в Mini App с ботом подпись «Отправить в чат» — файл придёт в чат. */
  toChat?: boolean
  dialog?: ToolDialog
  to?: string
  soon?: boolean
}

// Реестр инструментов: новый — одна строка. Порядок как у HitVid.
const TOOLS: ToolCard[] = [
  { id: 'analyze', icon: ScanSearch, title: 'Разбор видео', text: 'Вставьте ссылку на Reel из Instagram — получите метрики, описание, транскрипцию, раскадровку и возможность скачать ролик.', action: 'Открыть', dialog: 'analyze' },
  { id: 'uniqueness', icon: WandSparkles, title: 'Уникализация', text: 'Загрузите свои ролики — вернём копии, которые площадка не свяжет с оригиналом, и подпись к каждой.', action: 'Открыть', to: '/tools/uniqueness' },
  { id: 'reels', icon: Download, title: 'Скачать Reels', text: 'Вставьте ссылку на Reel из Instagram — и скачайте mp4 на устройство в один клик. В Mini App ролик придёт в чат с ботом.', action: 'Скачать', toChat: true, dialog: 'reels' },
  { id: 'youtube', icon: MonitorPlay, title: 'Скачать с YouTube', text: 'Вставьте ссылки на видео с YouTube — выберите качество вплоть до оригинального и скачайте mp4 или mp3.', action: 'Скачать', toChat: true, dialog: 'youtube' },
]

const DIALOGS: readonly string[] = ['analyze', 'reels', 'youtube'] satisfies ToolDialog[]

function isDialog(value: string | null): value is ToolDialog {
  return value !== null && DIALOGS.includes(value)
}

export function ToolsPage() {
  const user = useUser()
  const desktop = useIsDesktop()
  const info = useToolsInfo()
  const toChat = isInTelegram() && info.data?.telegram === true
  // `/tools?tool=analyze&url=…` открывает окно с готовой ссылкой (кнопка из бота),
  // `&job=<id>` — с задачей: обновление WebView и повторное открытие её не теряют.
  const [params, setParams] = useSearchParams()
  const fromLink = params.get('tool')
  const [dialog, setDialog] = useViewState<ToolDialog | null>('tools.dialog', isDialog(fromLink) ? fromLink : null)
  const active = useViewIsActive()
  const closeGraphs = useBack('/tools')
  const stateKey = `tools.${dialog}`
  const initial = isDialog(fromLink) ? (params.get('url') ?? '') : ''
  const initialJob = fromLink === 'analyze' ? params.get('job') : null
  useEffect(() => {
    if (isDialog(fromLink)) setDialog(fromLink)
  }, [fromLink])

  const close = useCallback(() => {
    setDialog(null)
    if (params.has('tool')) setParams({}, { replace: true })
  }, [params, setParams, setDialog])

  const rememberJob = useCallback(
    (id: string) => setParams({ tool: 'analyze', job: id }, { replace: true }),
    [setParams],
  )

  // На телефоне недавние задачи — над карточками: «Открыть разбор» без прокрутки.
  const recent = <RecentJobs className={desktop ? 'mt-6' : 'mb-6'} />
  return (
    <>
      <PageHeader title="Инструменты" description="Разбор чужого контента и скачивание видео." />
      {user.role === 'owner' && <PendingScreenshotsCard />}
      {user.role === 'owner' && <Link to="/analytics" className="mb-4 flex items-center gap-3 rounded-xl border border-line bg-card p-4 hover:bg-surface/60"><ChartNoAxesCombined size={22} className="shrink-0 text-muted" /><span className="min-w-0 flex-1"><strong className="block text-sm font-semibold">Аналитика Instagram</strong><span className="mt-1 block text-sm text-muted">Свои Reels, подписки, удержание и гипотезы.</span></span><ArrowRight size={18} className="shrink-0 text-muted" /></Link>}
      {!desktop && recent}
      <ul className="grid divide-y divide-line rounded-xl bg-card ring-1 ring-line desk:grid-cols-2 desk:gap-3 desk:divide-y-0 desk:bg-transparent desk:ring-0">
        {TOOLS.map((tool) => (
          <li key={tool.id}>
            <ToolTile tool={tool} toChat={toChat} onOpen={setDialog} />
          </li>
        ))}
      </ul>
      {desktop && recent}
      {active && dialog === 'analyze' && (
        <AnalyzeDialog stateKey={stateKey} initial={initial} initialJob={initialJob} onJob={rememberJob} onClose={close} />
      )}
      {active && dialog === 'reels' && <ReelDialog stateKey={stateKey} initial={initial} onClose={close} />}
      {active && dialog === 'youtube' && <YoutubeDialog stateKey={stateKey} initial={initial} onClose={close} />}
      {active && user.role === 'owner' && params.get('graphs') === 'missing' && <ScreenshotQueueDialog onClose={closeGraphs} />}
    </>
  )
}

function ToolTile({ tool, toChat, onOpen }: { tool: ToolCard; toChat: boolean; onOpen: (dialog: ToolDialog) => void }) {
  const { icon: Icon, title, text, soon } = tool
  const action = tool.toChat && toChat ? 'Отправить в чат' : tool.action
  const body = (
    <>
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface text-link transition-colors group-hover:bg-accent group-hover:text-accent-fg">
        <Icon size={18} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2">
          <span className="font-semibold">{title}</span>
          {soon && <span className="rounded bg-surface px-1.5 py-0.5 text-[11px] leading-none text-muted">скоро</span>}
        </span>
        <span className="mt-1 text-sm text-pretty text-muted">{text}</span>
        {!soon && (
          <span className="mt-2.5 inline-flex items-center gap-1 text-sm font-medium">
            {action}
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        )}
      </span>
    </>
  )
  const tile = 'group flex h-full w-full items-start gap-3 rounded-xl p-3.5 text-left hover:bg-surface/60 desk:bg-card desk:p-4 desk:ring-1 desk:ring-line'
  if (soon) return <div className={tile}>{body}</div>
  if (tool.to)
    return (
      <Link to={tool.to} className={`${tile} hover:ring-fg/25`}>
        {body}
      </Link>
    )
  const dialog = tool.dialog
  return (
    <button type="button" className={`${tile} hover:ring-fg/25`} onClick={() => dialog && onOpen(dialog)}>
      {body}
    </button>
  )
}
